/**
 * BankImportWizard - 3-step CSV bank transaction import
 * Step 1: Upload CSV file
 * Step 2: Map columns (date, amount, description)
 * Step 3: Review & approve transactions
 */

import React, { useState, useRef, useCallback } from 'react';
import { Account, CSVUploadResponse, ColumnMapping, ImportedTransaction } from '../../../types/accounting';
import { accountingApi } from '../../../services/accountingApi';
import '../../../styles/BankImport.css';

interface BankImportWizardProps {
  businessId: number;
  accounts: Record<string, Account[]>;
  currency: 'CAD' | 'USD';
  onComplete: () => void;
  onCancel: () => void;
}

type WizardStep = 1 | 2 | 3;

const BANK_TEMPLATES = ['RBC', 'TD', 'Scotiabank', 'BMO', 'CIBC', 'Custom'] as const;

const BankImportWizard: React.FC<BankImportWizardProps> = ({
  businessId,
  accounts,
  currency,
  onComplete,
  onCancel
}) => {
  // Wizard state
  const [step, setStep] = useState<WizardStep>(1);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Step 1: Upload state
  const [selectedBank, setSelectedBank] = useState<string>('Custom');
  const [dragOver, setDragOver] = useState(false);
  const [uploadResult, setUploadResult] = useState<CSVUploadResponse | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Step 2: Mapping state
  const [mapping, setMapping] = useState<ColumnMapping>({
    date: '',
    amount: '',
    description: '',
    category: '',
    reference: '',
    amount_type: 'single',
    debit_column: '',
    credit_column: ''
  });

  // Step 3: Review state
  const [transactions, setTransactions] = useState<ImportedTransaction[]>([]);
  const [selectedIds, setSelectedIds] = useState<Set<number>>(new Set());
  const [categoryOverrides, setCategoryOverrides] = useState<Record<number, number>>({});
  const [importComplete, setImportComplete] = useState(false);
  const [importSummary, setImportSummary] = useState<{ count: number; entries: number } | null>(null);

  // All account types flattened for category dropdown
  const allAccounts: Account[] = (Object.values(accounts) as Account[][]).flat();

  // ===================== STEP 1: Upload =====================

  const handleFileSelect = async (file: File) => {
    if (!file.name.endsWith('.csv')) {
      setError('Please select a CSV file');
      return;
    }

    setError(null);
    setLoading(true);

    try {
      const response = await accountingApi.uploadCSV(businessId, file);

      if (response.status === 'success' && response.data) {
        setUploadResult(response.data);

        // Pre-fill mapping from detected columns
        const detected = response.data.detected_columns;
        setMapping(prev => ({
          ...prev,
          date: detected.date || '',
          amount: detected.amount || '',
          description: detected.description || ''
        }));

        // Auto-select bank if detected
        if (response.data.suggested_bank) {
          setSelectedBank(response.data.suggested_bank);
        }
      } else {
        setError('Failed to process CSV file');
      }
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to upload file');
    } finally {
      setLoading(false);
    }
  };

  const handleDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setDragOver(false);
    const file = e.dataTransfer.files[0];
    if (file) handleFileSelect(file);
  }, []);

  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setDragOver(true);
  }, []);

  const handleDragLeave = useCallback(() => {
    setDragOver(false);
  }, []);

  const handleBrowseClick = () => {
    fileInputRef.current?.click();
  };

  const handleFileInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) handleFileSelect(file);
  };

  // ===================== STEP 2: Map Columns =====================

  const handleMappingChange = (field: keyof ColumnMapping, value: string) => {
    setMapping(prev => ({ ...prev, [field]: value }));
  };

  const isMappingValid = (): boolean => {
    if (!mapping.date || !mapping.description) return false;
    if (mapping.amount_type === 'single' && !mapping.amount) return false;
    if (mapping.amount_type === 'split' && (!mapping.debit_column || !mapping.credit_column)) return false;
    return true;
  };

  const applyMapping = async () => {
    if (!uploadResult) return;

    setError(null);
    setLoading(true);

    try {
      await accountingApi.mapColumns(businessId, uploadResult.import_id, mapping);

      // Auto-categorize
      await accountingApi.categorizeTransactions(businessId, uploadResult.import_id);

      // Load transactions
      const response = await accountingApi.getImportTransactions(businessId, uploadResult.import_id);

      if (response.status === 'success' && response.data?.transactions) {
        setTransactions(response.data.transactions);
        // Select all by default
        setSelectedIds(new Set(response.data.transactions.map(t => t.id)));
        setStep(3);
      } else {
        setError('Failed to load transactions after mapping');
      }
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to apply column mapping');
    } finally {
      setLoading(false);
    }
  };

  // ===================== STEP 3: Review & Approve =====================

  const toggleSelect = (id: number) => {
    setSelectedIds(prev => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });
  };

  const selectAll = () => {
    setSelectedIds(new Set(transactions.map(t => t.id)));
  };

  const deselectAll = () => {
    setSelectedIds(new Set());
  };

  const handleCategoryChange = (transactionId: number, accountId: number) => {
    setCategoryOverrides(prev => ({ ...prev, [transactionId]: accountId }));
  };

  const approveSelected = async () => {
    if (selectedIds.size === 0) {
      setError('Select at least one transaction to import');
      return;
    }

    setError(null);
    setLoading(true);

    try {
      const response = await accountingApi.approveTransactions(
        businessId,
        uploadResult!.import_id,
        Array.from(selectedIds)
      );

      if (response.status === 'success' && response.data) {
        setImportComplete(true);
        setImportSummary({
          count: response.data.approved_count,
          entries: response.data.journal_entries_created
        });
      } else {
        setError('Failed to approve transactions');
      }
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to import transactions');
    } finally {
      setLoading(false);
    }
  };

  // ===================== Format helpers =====================

  const locale = currency === 'CAD' ? 'en-CA' : 'en-US';
  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat(locale, {
      style: 'currency',
      currency: currency
    }).format(Math.abs(amount));
  };

  const getConfidenceClass = (confidence: number): string => {
    if (confidence >= 0.8) return 'confidence-high';
    if (confidence >= 0.5) return 'confidence-medium';
    return 'confidence-low';
  };

  // ===================== Render =====================

  // Success screen
  if (importComplete && importSummary) {
    return (
      <div className="bank-import-wizard">
        <div className="import-success">
          <h3>Import Complete</h3>
          <p>
            {importSummary.count} transactions imported as {importSummary.entries} journal entries.
          </p>
          <div style={{ display: 'flex', gap: '12px', justifyContent: 'center' }}>
            <button className="btn-wizard-secondary" onClick={onCancel}>
              Back to Dashboard
            </button>
            <button className="btn-wizard-success" onClick={onComplete}>
              Done
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="bank-import-wizard">
      {/* Step Indicator */}
      <div className="wizard-steps">
        <div className={`wizard-step ${step === 1 ? 'active' : step > 1 ? 'completed' : ''}`}>
          <span className="step-number">{step > 1 ? '\u2713' : '1'}</span>
          <span>Upload CSV</span>
        </div>
        <div className={`wizard-step ${step === 2 ? 'active' : step > 2 ? 'completed' : ''}`}>
          <span className="step-number">{step > 2 ? '\u2713' : '2'}</span>
          <span>Map Columns</span>
        </div>
        <div className={`wizard-step ${step === 3 ? 'active' : ''}`}>
          <span className="step-number">3</span>
          <span>Review & Import</span>
        </div>
      </div>

      {/* Error display */}
      {error && (
        <div className="import-alert error">{error}</div>
      )}

      {/* ========= STEP 1: Upload ========= */}
      {step === 1 && (
        <div>
          <h3 style={{ marginTop: 0, marginBottom: '16px' }}>Upload Bank Statement</h3>

          {/* Bank selector */}
          <label style={{ display: 'block', marginBottom: '8px', fontSize: '13px', fontWeight: 600, color: '#374151' }}>
            Bank Template (optional)
          </label>
          <div className="bank-selector">
            {BANK_TEMPLATES.map(bank => (
              <button
                key={bank}
                className={`bank-option ${selectedBank === bank ? 'selected' : ''}`}
                onClick={() => setSelectedBank(bank)}
              >
                {bank}
              </button>
            ))}
          </div>

          {/* Drag & drop zone */}
          <div
            className={`dropzone ${dragOver ? 'drag-over' : ''}`}
            onDrop={handleDrop}
            onDragOver={handleDragOver}
            onDragLeave={handleDragLeave}
            onClick={handleBrowseClick}
          >
            <input
              ref={fileInputRef}
              type="file"
              accept=".csv"
              onChange={handleFileInputChange}
              style={{ display: 'none' }}
            />
            {loading ? (
              <>
                <div className="dropzone-icon">...</div>
                <div className="dropzone-text">Processing file...</div>
              </>
            ) : (
              <>
                <div className="dropzone-icon">CSV</div>
                <div className="dropzone-text">
                  Drag & drop your CSV file here
                </div>
                <div className="dropzone-subtext">
                  or <span className="dropzone-browse">browse</span> to select a file
                </div>
              </>
            )}
          </div>

          {/* Preview table (after upload) */}
          {uploadResult && (
            <div style={{ marginTop: '20px' }}>
              <div className="file-info">
                {uploadResult.filename} - {uploadResult.total_rows} rows detected
              </div>

              <h4 style={{ marginBottom: '8px', fontSize: '14px', color: '#374151' }}>
                Preview (first 5 rows)
              </h4>
              <div className="preview-table-container">
                <table className="preview-table">
                  <thead>
                    <tr>
                      {uploadResult.headers.map((header, i) => (
                        <th key={i}>{header}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {uploadResult.preview_rows.map((row, rowIdx) => (
                      <tr key={rowIdx}>
                        {row.map((cell, cellIdx) => (
                          <td key={cellIdx}>{cell}</td>
                        ))}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              <div className="wizard-buttons">
                <button className="btn-wizard-secondary" onClick={onCancel}>
                  Cancel
                </button>
                <button className="btn-wizard-primary" onClick={() => setStep(2)}>
                  Next: Map Columns
                </button>
              </div>
            </div>
          )}

          {/* Cancel button when no file uploaded yet */}
          {!uploadResult && (
            <div className="wizard-buttons">
              <button className="btn-wizard-secondary" onClick={onCancel}>
                Cancel
              </button>
            </div>
          )}
        </div>
      )}

      {/* ========= STEP 2: Map Columns ========= */}
      {step === 2 && uploadResult && (
        <div>
          <h3 style={{ marginTop: 0, marginBottom: '8px' }}>Map Columns</h3>
          <p style={{ color: '#6b7280', fontSize: '14px', marginBottom: '20px' }}>
            Tell us which columns contain the transaction date, amount, and description.
          </p>

          {/* Amount type toggle */}
          <div style={{ marginBottom: '20px' }}>
            <label style={{ display: 'block', marginBottom: '8px', fontSize: '13px', fontWeight: 600, color: '#374151' }}>
              Amount Format
            </label>
            <div style={{ display: 'flex', gap: '12px' }}>
              <label style={{ display: 'flex', alignItems: 'center', gap: '6px', cursor: 'pointer', fontSize: '14px' }}>
                <input
                  type="radio"
                  checked={mapping.amount_type === 'single'}
                  onChange={() => handleMappingChange('amount_type', 'single')}
                />
                Single amount column (+/-)
              </label>
              <label style={{ display: 'flex', alignItems: 'center', gap: '6px', cursor: 'pointer', fontSize: '14px' }}>
                <input
                  type="radio"
                  checked={mapping.amount_type === 'split'}
                  onChange={() => handleMappingChange('amount_type', 'split')}
                />
                Separate debit/credit columns
              </label>
            </div>
          </div>

          {/* Column mapping dropdowns */}
          <div className="column-mapper">
            {/* Date */}
            <div className="mapper-field">
              <label>
                Date Column <span className="required-star">*</span>
                {uploadResult.detected_columns.date === mapping.date && mapping.date && (
                  <span className="auto-detect-badge"> (auto-detected)</span>
                )}
              </label>
              <select
                value={mapping.date}
                onChange={(e) => handleMappingChange('date', e.target.value)}
                className={uploadResult.detected_columns.date === mapping.date && mapping.date ? 'auto-detected' : ''}
              >
                <option value="">-- Select column --</option>
                {uploadResult.headers.map(header => (
                  <option key={header} value={header}>{header}</option>
                ))}
              </select>
            </div>

            {/* Description */}
            <div className="mapper-field">
              <label>
                Description Column <span className="required-star">*</span>
                {uploadResult.detected_columns.description === mapping.description && mapping.description && (
                  <span className="auto-detect-badge"> (auto-detected)</span>
                )}
              </label>
              <select
                value={mapping.description}
                onChange={(e) => handleMappingChange('description', e.target.value)}
                className={uploadResult.detected_columns.description === mapping.description && mapping.description ? 'auto-detected' : ''}
              >
                <option value="">-- Select column --</option>
                {uploadResult.headers.map(header => (
                  <option key={header} value={header}>{header}</option>
                ))}
              </select>
            </div>

            {/* Amount (single) or Debit/Credit (split) */}
            {mapping.amount_type === 'single' ? (
              <div className="mapper-field">
                <label>
                  Amount Column <span className="required-star">*</span>
                  {uploadResult.detected_columns.amount === mapping.amount && mapping.amount && (
                    <span className="auto-detect-badge"> (auto-detected)</span>
                  )}
                </label>
                <select
                  value={mapping.amount}
                  onChange={(e) => handleMappingChange('amount', e.target.value)}
                  className={uploadResult.detected_columns.amount === mapping.amount && mapping.amount ? 'auto-detected' : ''}
                >
                  <option value="">-- Select column --</option>
                  {uploadResult.headers.map(header => (
                    <option key={header} value={header}>{header}</option>
                  ))}
                </select>
              </div>
            ) : (
              <>
                <div className="mapper-field">
                  <label>Debit Column <span className="required-star">*</span></label>
                  <select
                    value={mapping.debit_column || ''}
                    onChange={(e) => handleMappingChange('debit_column', e.target.value)}
                  >
                    <option value="">-- Select column --</option>
                    {uploadResult.headers.map(header => (
                      <option key={header} value={header}>{header}</option>
                    ))}
                  </select>
                </div>
                <div className="mapper-field">
                  <label>Credit Column <span className="required-star">*</span></label>
                  <select
                    value={mapping.credit_column || ''}
                    onChange={(e) => handleMappingChange('credit_column', e.target.value)}
                  >
                    <option value="">-- Select column --</option>
                    {uploadResult.headers.map(header => (
                      <option key={header} value={header}>{header}</option>
                    ))}
                  </select>
                </div>
              </>
            )}

            {/* Optional: Category */}
            <div className="mapper-field">
              <label>Category Column <span style={{ fontWeight: 400, color: '#9ca3af' }}>(optional)</span></label>
              <select
                value={mapping.category || ''}
                onChange={(e) => handleMappingChange('category', e.target.value)}
              >
                <option value="">-- None --</option>
                {uploadResult.headers.map(header => (
                  <option key={header} value={header}>{header}</option>
                ))}
              </select>
            </div>

            {/* Optional: Reference */}
            <div className="mapper-field">
              <label>Reference / Check # <span style={{ fontWeight: 400, color: '#9ca3af' }}>(optional)</span></label>
              <select
                value={mapping.reference || ''}
                onChange={(e) => handleMappingChange('reference', e.target.value)}
              >
                <option value="">-- None --</option>
                {uploadResult.headers.map(header => (
                  <option key={header} value={header}>{header}</option>
                ))}
              </select>
            </div>
          </div>

          {/* Preview with current mapping */}
          <h4 style={{ marginBottom: '8px', fontSize: '14px', color: '#374151' }}>
            Mapped Preview
          </h4>
          <div className="preview-table-container">
            <table className="preview-table">
              <thead>
                <tr>
                  <th>Date</th>
                  <th>Description</th>
                  <th>Amount</th>
                  {mapping.category && <th>Category</th>}
                  {mapping.reference && <th>Reference</th>}
                </tr>
              </thead>
              <tbody>
                {uploadResult.preview_rows.map((row, rowIdx) => {
                  const dateIdx = uploadResult.headers.indexOf(mapping.date);
                  const descIdx = uploadResult.headers.indexOf(mapping.description);
                  const amtIdx = uploadResult.headers.indexOf(mapping.amount);
                  const catIdx = mapping.category ? uploadResult.headers.indexOf(mapping.category) : -1;
                  const refIdx = mapping.reference ? uploadResult.headers.indexOf(mapping.reference) : -1;

                  return (
                    <tr key={rowIdx}>
                      <td>{dateIdx >= 0 ? row[dateIdx] : '--'}</td>
                      <td>{descIdx >= 0 ? row[descIdx] : '--'}</td>
                      <td>{amtIdx >= 0 ? row[amtIdx] : '--'}</td>
                      {mapping.category && <td>{catIdx >= 0 ? row[catIdx] : '--'}</td>}
                      {mapping.reference && <td>{refIdx >= 0 ? row[refIdx] : '--'}</td>}
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          <div className="wizard-buttons">
            <button className="btn-wizard-secondary" onClick={() => setStep(1)}>
              Back
            </button>
            <button
              className="btn-wizard-primary"
              onClick={applyMapping}
              disabled={!isMappingValid() || loading}
            >
              {loading ? 'Applying...' : 'Apply Mapping'}
            </button>
          </div>
        </div>
      )}

      {/* ========= STEP 3: Review & Approve ========= */}
      {step === 3 && (
        <div>
          <h3 style={{ marginTop: 0, marginBottom: '8px' }}>Review Transactions</h3>
          <p style={{ color: '#6b7280', fontSize: '14px', marginBottom: '16px' }}>
            Review the imported transactions, adjust categories if needed, then approve.
          </p>

          {/* Bulk actions */}
          <div className="bulk-actions">
            <span className="selected-count">
              {selectedIds.size} of {transactions.length} selected
            </span>
            <button className="btn-wizard-secondary" onClick={selectAll} style={{ padding: '6px 14px' }}>
              Select All
            </button>
            <button className="btn-wizard-secondary" onClick={deselectAll} style={{ padding: '6px 14px' }}>
              Deselect All
            </button>
          </div>

          {/* Transaction table */}
          <div className="review-table-container" style={{ maxHeight: '500px', overflowY: 'auto' }}>
            <table className="review-table">
              <thead>
                <tr>
                  <th style={{ width: '40px' }}>
                    <input
                      type="checkbox"
                      checked={selectedIds.size === transactions.length && transactions.length > 0}
                      onChange={() => selectedIds.size === transactions.length ? deselectAll() : selectAll()}
                    />
                  </th>
                  <th>Date</th>
                  <th>Description</th>
                  <th style={{ textAlign: 'right' }}>Amount</th>
                  <th>Suggested Category</th>
                  <th style={{ width: '60px', textAlign: 'center' }}>Confidence</th>
                </tr>
              </thead>
              <tbody>
                {transactions.map(txn => {
                  const isSelected = selectedIds.has(txn.id);
                  const overrideAccountId = categoryOverrides[txn.id];

                  return (
                    <tr key={txn.id} className={isSelected ? 'selected' : ''}>
                      <td>
                        <input
                          type="checkbox"
                          checked={isSelected}
                          onChange={() => toggleSelect(txn.id)}
                        />
                      </td>
                      <td>{txn.transaction_date}</td>
                      <td>{txn.description}</td>
                      <td style={{ textAlign: 'right' }}>
                        <span className={txn.is_debit ? 'amount-expense' : 'amount-deposit'}>
                          {txn.is_debit ? '-' : '+'}{formatCurrency(txn.amount)}
                        </span>
                      </td>
                      <td>
                        <select
                          className="category-select"
                          value={overrideAccountId || txn.account_id || ''}
                          onChange={(e) => handleCategoryChange(txn.id, parseInt(e.target.value))}
                        >
                          <option value="">Uncategorized</option>
                          {allAccounts.map(account => (
                            <option key={account.id} value={account.id}>
                              {account.account_name}
                            </option>
                          ))}
                        </select>
                      </td>
                      <td style={{ textAlign: 'center' }}>
                        {txn.category_confidence > 0 && (
                          <span className={`confidence-badge ${getConfidenceClass(txn.category_confidence)}`}>
                            {Math.round(txn.category_confidence * 100)}%
                          </span>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          {transactions.length === 0 && (
            <div className="import-alert info">
              No transactions found. Try adjusting your column mapping.
            </div>
          )}

          <div className="wizard-buttons">
            <button className="btn-wizard-secondary" onClick={() => setStep(2)}>
              Back
            </button>
            <button className="btn-wizard-secondary" onClick={onCancel}>
              Cancel
            </button>
            <button
              className="btn-wizard-success"
              onClick={approveSelected}
              disabled={selectedIds.size === 0 || loading}
            >
              {loading
                ? 'Importing...'
                : `Import ${selectedIds.size} Transaction${selectedIds.size !== 1 ? 's' : ''}`
              }
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default BankImportWizard;
